import { useId, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  getFileExtension,
  getFileNameWithoutExtension
} from "shared/lib/file/getFileExtension"
import { useEvent } from "shared/lib/useEvent"
import { AttachmentTypename } from "shared/types/Attachment"
import { FilePreview } from "shared/types/File"
import {
  AttachmentType,
  ProjectImportError
} from "shared/types/__generated__/graphql"

import { zodResolver } from "@hookform/resolvers/zod"
import {
  ProjectFormData,
  ProjectFormDataSchema,
  ProjectFormState
} from "features/project-management/types/ProjectFormData"

import { NewProjectOverlayProps } from "../NewProjectOverlay"
import { NewProjectOverlayViewModel } from "../NewProjectOverlayView"

export function useNewProjectOverlayViewModel({
  defaultFormData,
  onSubmit,
  onCancel,
  onXlsxUpload,
  onXlsxDelete: onXlsxDeleteProp,
  xlsxExampleUrl,
  isXlsxUploadDisabled,
  isSubmitting,
  ...rest
}: NewProjectOverlayProps): NewProjectOverlayViewModel {
  const { t } = useTranslation()
  const xlsxUploadErrorHintId = useId()
  const xlsxImportErrorHintId = useId()
  const [xlsxUploadError, setXlsxUploadError] = useState<string | undefined>(
    undefined
  )
  const [xlsxImportErrors, setXlsxImportErrors] = useState<
    ProjectImportError[] | undefined
  >(undefined)
  const [xlsxFile, setXlsxFile] = useState<AttachmentType | undefined>(
    undefined
  )

  const form = useForm({
    defaultValues: defaultFormData,
    resolver: zodResolver<ProjectFormState, unknown, ProjectFormData>(
      ProjectFormDataSchema
    )
  })

  const handleCancel = useEvent(() => {
    onCancel?.()
    form.reset(defaultFormData)
    setXlsxFile(undefined)
    setXlsxUploadError(undefined)
    setXlsxImportErrors(undefined)
  })

  const handleSubmit = useEvent(async (formData: ProjectFormData) => {
    const result = await onSubmit?.(formData)
    if (result?.errors) {
      setXlsxImportErrors(result.errors)
    }
  })

  const memoizedSubmit = useMemo(
    () => form.handleSubmit(handleSubmit),
    [handleSubmit]
  )

  const handleXlsxDelete = useEvent(() => {
    setXlsxFile(undefined)
    onXlsxDeleteProp?.()
  })

  const handleXlsxUploadComplete = useEvent(
    (successes: FilePreview[], failures: FilePreview[]) => {
      if (failures.length > 0) {
        setXlsxUploadError(t("NewProjectOverlay.XlsxUpload.Error"))
        setXlsxFile(undefined)
      } else {
        setXlsxUploadError(undefined)
        const uploaded = successes[0]
        if (uploaded) {
          setXlsxFile({
            id: uploaded.id,
            fileName: getFileNameWithoutExtension(uploaded.file.name),
            readUrl: URL.createObjectURL(uploaded.file),
            name: getFileNameWithoutExtension(uploaded.file.name),
            extension: getFileExtension(uploaded.file.name),
            __typename: AttachmentTypename
          })
        }
      }
    }
  )

  return {
    ...rest,
    form,
    isSubmitting,
    onCancel: handleCancel,
    onSubmit: memoizedSubmit,
    onXlsxUpload,
    onXlsxUploadComplete: handleXlsxUploadComplete,
    onXlsxDelete: handleXlsxDelete,
    xlsxExampleUrl,
    xlsxUploadError,
    xlsxUploadErrorHintId,
    xlsxImportErrors,
    xlsxImportErrorHintId,
    xlsxFile,
    isXlsxUploadDisabled
  }
}
